// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import React, { useEffect, useState } from "react"
import { useLocales } from "react-localized"
import ReactMarkdown from 'react-markdown'
import {
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonFooter,
  IonGrid,
  IonModal,
  IonRow,
  IonSpinner,
  IonText,
  IonToolbar,
} from "@ionic/react"
import { faSortDown } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IAccount } from "../../../api"
import './AccountSelector.scss'
import { Conditional, LoginButton, Table, useSession } from "../../../lib"
import { useAccount } from "../context"


interface AccountSelectorProps {
  accounts: {[key: number | string]: IAccount} | null
  activeAccount: IAccount | null
  setActiveAccount(account: IAccount): void
}


const params: URLSearchParams = new URLSearchParams(window.location.search)
const accountId: number | null = (!!params.get("rlm"))
  ? parseInt(params.get("rlm") as string)
  : null


const AccountSelector: React.FC<AccountSelectorProps> = ({
  accounts,
  activeAccount,
  setActiveAccount
}) => {
  const canSelect = accounts !== null && Object.keys(accounts).length > 1
  const hasAccount = Object.values(accounts || {}).length >= 1
  const {gettext} = useLocales()
  const {user} = useSession()
  const {mustSelectAccount} = useAccount()
  const [selected, setSelected] = useState<IAccount | null>(null)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const changeProjectUrl: (selected: IAccount) => string = (selected) => {
    const url = new URL(window.location.href)
    url.searchParams.set("rlm", selected.getIdentifier().toString())
    return url.toString()
  }
  const onSelected: (selected: IAccount | null) => void = (selected) => {
    if (selected === null) { return }
    window.location.href = changeProjectUrl(selected)
  }

  useEffect(() => {
    if (mustSelectAccount && canSelect) {
      setIsOpen(true)
      return
    }
    if (!accounts || accountId === null || !accounts[accountId]) {
      return
    }
    setActiveAccount(accounts[accountId])
  }, [accounts])

  useEffect(() => {
    if (!isOpen) {
      setSelected(null)
    }
  }, [isOpen])
  return (
    <div
      className="account-selector-wrapper"
    >
      <IonModal
        backdropDismiss={!mustSelectAccount}
        className="accounts"
        isOpen={isOpen}
        onDidDismiss={() => setIsOpen(false)}
      >
        <IonContent>
          <IonGrid>
            <IonRow style={{height: "64px"}}>
              <IonCol>
                <h2>{gettext("Select an account to work with")}</h2>
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol>
                <IonText style={{fontSize: "13px"}}>
                  <p>
                    <span>{gettext("Accounts are linked to your email address.")} </span>
                    <span>{gettext("Don't see the account(s) you're expecting?")} </span>
                    <span>{gettext("Try signing in with a different email address.")} </span>
                    <span className="bold-primary">
                      <ReactMarkdown components={{p: React.Fragment}}>
                      {gettext`The email address you used to sign in is **${(!!user.isAuthenticated()) ? user.getEmail() : ""}**.`}
                      </ReactMarkdown>
                    </span>
                  </p>
                </IonText>
              </IonCol>
            </IonRow>
            <IonRow>
              <Table
                className="transparent"
                columns={[
                  {
                    key: "name",
                    label: gettext("Name"),
                    width: 30,
                    render: (row: IAccount, index: number) => {
                      return (
                        <a href={changeProjectUrl(row)}>{row.getDisplayName()}</a>
                      )
                    }
                  },
                  {
                    key: "id",
                    label: gettext("ID"),
                    width: 30,
                    render: (row: IAccount, index: number) => {
                      return row.getIdentifier().toString()
                    }
                  },
                ]}
                items={(!!accounts) ? Object.values(accounts) : []}
                onRowClick={(value: IAccount, index: number) => {
                  setSelected(value)
                }}
              />
            </IonRow>
          </IonGrid>
        </IonContent>
        <IonFooter>
          <IonToolbar>
            <IonButtons slot="start">
              <LoginButton color="danger" fill="clear" params={{prompt: "select_account"}}>
                {gettext("Change email")}
              </LoginButton>
            </IonButtons>
            <IonButtons slot="end">
              <IonButton
                disabled={mustSelectAccount}
                onClick={() => setIsOpen(false)}
                style={{visibility: (mustSelectAccount) ? "hidden" : "visible"}}
              >
                {gettext("Close")}
              </IonButton>
              <IonButton
                color={(!selected) ? "medium" : "primary"}
                disabled={!selected}
                onClick={() => onSelected(selected)}
              >
                {gettext("Select account")}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonFooter>
      </IonModal>
      <div className="account-selector" onClick={() => {
          if (accounts !== null && Object.keys(accounts).length === 0) {
            return
          }
          setIsOpen(true)
        }}>
        <div className="accounts">
          <Conditional render={accounts === null}>
            <IonSpinner name="dots"/>
          </Conditional>
            {(activeAccount !== null) ? activeAccount.getDisplayName() : activeAccount}
        </div>
        <div className="icon">
          <FontAwesomeIcon icon={faSortDown}/>
        </div>
      </div>
    </div>
  )
}


export { AccountSelector }